import { createSelector } from 'reselect';
import isPointInShape from '@doveconviene/sf-web-js-sdk/isPointInShape';
import {
  sortBy,
  replaceRegExp,
  getFlyerId,
  doesFlyerCustomLabelMatch,
  getRetailerLogo,
  isStoreAnnouncement,
} from 'utils';
import { selectNearbyGeolocation } from 'flux/selectors/user.selectors';
import { selectFlyerGib } from 'flux/selectors/flyerGibs.selectors';
import { selectQuery, selectTag } from 'flux/selectors/tags.selectors';
import {
  selectPropertyMultiRetailerMode,
  selectPropertyEndDateHidden,
} from 'flux/selectors/property.selectors';
// import { selectCustomLabel } from 'flux/selectors/application.selectors';
import sortByPriorityAndDistance from 'utils/sortByPriorityAndDistance';
import { selectIsMobile } from 'flux/selectors/ui.selectors';
import { getBottomRetailMediaBannerSize } from 'utils/retailMediaBanners';
// import { selectCurrentStoreId } from 'flux/selectors/stores.selectors';
// import { selectStoreIdReadingReady } from 'flux/selectors/router.selectors';

//flyers list or currentFlyer
export const selectFlyerFromListOrCurrentFlyer = (state, flyerId) => {
  let flyer = selectCurrentFlyer(state);
  if (!flyer || flyer.id !== flyerId) {
    flyer = selectFlyer(state, flyerId);
  }
  return flyer;
};

// Selectors
export const selectFetchedLocation = (state) => state.flyers.fetchedLocation;

export const selectFlyers = (state) => state.flyers.list;

export const selectFlyersArray = createSelector([selectFlyers], (flyersObject) =>
  Object.values(flyersObject),
);

// export const selectSingleFlyerIdByCustomLabel = createSelector(
//   [selectStoreIdReadingReady, selectCurrentStoreId, selectCustomLabel, selectFlyersArray],
//   (storeIdReadingReady, currentStoreId, customLabel, flyersArray) => {
//     if (!storeIdReadingReady) return null;

//     let flyers = flyersArray.filter((flyer) => doesFlyerCustomLabelMatch(flyer, customLabel));

//     if (currentStoreId) {
//       flyers = flyers.filter((flyer) => flyer.stores && flyer.stores[currentStoreId]);
//     }

//     const warnings = [];
//     const errors = [];

//     if (flyers.length === 0) {
//       if (currentStoreId) {
//         errors.push(`When looking for a flyer with custom label "${customLabel}" in store "${currentStoreId}", NO flyers were found.`);
//       } else {
//         errors.push(`When looking for a flyer with custom label "${customLabel}", NO flyers were found.`);
//       }

//       return {
//         flyerId: null,
//         errors,
//         warnings,
//       };
//     }

//     if (flyers.length > 1) {
//       const ids = flyers.map((flyer) => getFlyerId(flyer));
//       const warningEnd = `more than one flyer was found: ${ids.join(', ')}.`;
//       if (currentStoreId) {
//         warnings.push(`When looking for a flyer with custom label "${customLabel}" in store "${currentStoreId}", ${warningEnd}`);
//       } else {
//         warnings.push(`When looking for a flyer with custom label "${customLabel}", ${warningEnd}`);
//       }
//     }

//     return {
//       flyerId: getFlyerId(flyers[0]),
//       errors,
//       warnings,
//     };
//   }
// );

export const selectFlyerIdsByCustomLabel = createSelector(
  [(_, params) => params, selectFlyersArray],
  (customLabel, flyersArray) =>
    flyersArray.filter((flyer) => doesFlyerCustomLabelMatch(flyer, customLabel)).map(getFlyerId),
);

export const selectFlyer = (state, id) => state.flyers.list[id] || null;

export const selectFlyerStoresIds = (state, flyerId) => {
  const flyer = selectFlyerFromListOrCurrentFlyer(state, flyerId);
  if (flyer && flyer.stores) {
    return Object.keys(flyer.stores);
  }
  return [];
};

export const selectFlyersFlyerGibs = (state) => state.flyers.flyerGibs;
export const selectFlyerFlyerGibsIds = (state, id) => {
  if (state.flyers.flyerGibs && id in state.flyers.flyerGibs) {
    return state.flyers.flyerGibs[id];
  }
  return [];
};

export const selectFlyerGibsChildIdsMap = (state) => state.flyers.flyerGibsChildIdsMap;

export const selectFlyerTagsIds = (state, flyerId) => {
  const flyerGibsIds = selectFlyerFlyerGibsIds(state, flyerId);
  return (
    flyerGibsIds
      .map((flyerGibId) => selectFlyerGib(state, flyerGibId))
      .filter(Boolean)
      // eslint-disable-next-line no-bitwise
      .filter(({ type }) => type && ~['clip', 'crop'].indexOf(type))
  );
};

export const selectFlyerTags = (state, flyerId) => {
  const flyerGibsIds = selectFlyerTagsIds(state, flyerId);

  return flyerGibsIds.map((flyerGib) => selectTag(state, flyerGib.id));
};

export const searchFlyerTags = (state, flyerId) => {
  const tags = selectFlyerTags(state, flyerId);

  const query = replaceRegExp(selectQuery(state));
  return tags.filter((tag) => tag && new RegExp(query, 'i').exec(tag.title));
};

// CurrentFlyer selectors
export const selectCurrentFlyerId = (state) => state?.flyers?.current?.id;

export const selectNotFoundFlyerId = (state) => state?.flyers?.notFoundFlyerId;

export const selectCurrentFlyer = (state) => {
  return state?.flyers?.current;
};

export const isCurrentFlyerSAType = (state) => {
  const currentFlyer = selectCurrentFlyer(state);
  return isStoreAnnouncement(currentFlyer);
};

export const selectRetailerFromFirst = (state) => {
  const isMultiRetailerMode = selectPropertyMultiRetailerMode(state);
  if (state && !isMultiRetailerMode) {
    const firstFlyer = Object.values(state.flyers.list)[0];
    return firstFlyer?.retailer;
  }
  return null;
};

export const selectCurrentFlyerTags = (state) => {
  const flyerId = selectCurrentFlyerId(state);
  return selectFlyerTags(state, flyerId);
};

// export const selectCurrentFlyerTagsCount = (state) => {
//   const flyerId = selectCurrentFlyerId(state);
//   const flyerGibsIds = selectFlyerTagsIds(state, flyerId);
//   return flyerGibsIds.length;
// };

export const selectCurrentFlyerStoresIds = (state) => {
  return state.flyers.currentFlyerStores || [];
};

export const selectCurrentFlyerStores = (state) => {
  return state.flyers.currentFlyerStores;
};

export const selectCurrentFlyerStoresCount = createSelector(
  selectCurrentFlyerStoresIds,
  (currentFlyerStoresIds) => currentFlyerStoresIds.length,
);

export const selectCurrentFlyerNearestStore = createSelector(
  selectCurrentFlyerStores,
  (currentFlyerStores) => {
    const stores = currentFlyerStores.sort(sortBy('distance', 'ASC'));
    return stores[0] || null;
  },
);

export const selectCurrentFlyerPhoneFromStore = createSelector(
  selectCurrentFlyer,
  // eslint-disable-next-line camelcase
  (currentFlyer) => currentFlyer?.settings?.ctas?.phone_from_store || {},
);

export const selectCurrentFlyerTableOfContents = createSelector(
  selectCurrentFlyer,
  // eslint-disable-next-line camelcase
  (currentFlyer) => currentFlyer?.settings?.table_of_contents || {},
);

// export const selectNearbyFlyers = (state) => state.flyers.nearbyFlyers || [];

export const selectNextFlyerTagId = (state, currentFlyerTagId) => {
  const currentFlyerId = selectCurrentFlyerId(state);
  const currentFlyerTags = searchFlyerTags(state, currentFlyerId);
  const currentFlyerTagIndex = currentFlyerTags.map((tag) => tag.id).indexOf(currentFlyerTagId);

  if (currentFlyerTagIndex < 0) return null;
  return currentFlyerTags[currentFlyerTagIndex + 1]
    ? currentFlyerTags[currentFlyerTagIndex + 1].id
    : null;
};

export const selectPreviousFlyerTagId = (state, currentFlyerTagId) => {
  const currentFlyerId = selectCurrentFlyerId(state);
  const currentFlyerTags = searchFlyerTags(state, currentFlyerId);
  const currentFlyerTagIndex = currentFlyerTags.map((tag) => tag.id).indexOf(currentFlyerTagId);

  if (currentFlyerTagIndex < 0) return null;
  return currentFlyerTags[currentFlyerTagIndex - 1]
    ? currentFlyerTags[currentFlyerTagIndex - 1].id
    : null;
};

export const selectFlyerRetailer = (state, flyerId) => {
  const flyer = selectFlyerFromListOrCurrentFlyer(state, flyerId);
  return flyer?.retailer;
};

// Filters & Storters
export const filterCurrentFlyerStores = (state) => {
  const flyer = selectCurrentFlyer(state);
  if (!flyer) {
    return null;
  }
  const { stores: flyerStores = null } = flyer;
  if (!flyerStores) {
    return null;
  }

  const user = selectNearbyGeolocation(state);
  const nearestStores = [];
  let hasOperatedShape = false;

  for (const [, store] of Object.entries(flyerStores)) {
    if (isPointInShape(user, store.shape)) {
      hasOperatedShape = true;
      nearestStores.push(store);
    }
  }

  if (nearestStores.length === 0) {
    for (const [, store] of Object.entries(flyerStores)) {
      nearestStores.push(store);
    }
  }

  const stores = nearestStores.sort(sortBy('distance', 'ASC'));

  if (!hasOperatedShape) {
    return stores.slice(0, 10);
  }

  if (stores.length > 0) return stores;

  return nearestStores;
};

export const filteredCurrentFlyerNearestStore = (state) => filterCurrentFlyerStores(state)?.[0];

export const nearestStoresByFlyerId = (state, flyerId) => {
  const flyer = selectFlyerFromListOrCurrentFlyer(state, flyerId);
  if (!flyer) return null;
  const { stores: flyerStores = null } = flyer;
  if (!flyerStores) return null;
  const nearestStores = Object.values(flyerStores).sort(sortBy('distance', 'ASC'));
  return nearestStores;
};

export const checkIfFlyerIsActive = (state, flyerId) => {
  const flyer = selectFlyerFromListOrCurrentFlyer(state, flyerId);
  const now = new Date();

  const unpublishDate = flyer?.settings?.time_zone?.unpublish_at_iso;

  let unpublishDateZoned;

  if (unpublishDate) {
    unpublishDateZoned = new Date(unpublishDate);
  } else {
    unpublishDateZoned = new Date(flyer.unpublish_at);
  }

  return now <= unpublishDateZoned;
};

// export const filterFlyersIdsByLocation = (state) => {
//   const currentStoreId = selectCurrentStoreId(state);

//   let flyerIds = [];
//   if (currentStoreId) {
//     const flyersObject = selectFlyers(state);

//     flyerIds = Object.keys(flyersObject).filter(
//       (flyerId) => flyersObject[flyerId]?.stores?.[currentStoreId]
//     );
//   } else {
//     const location = selectNearbyGeolocation(state);
//     const shapes = selectFlyersShapes(state);

//     flyerIds = Object.keys(shapes).filter((flyerId) =>
//       isPointInShape(location, shapes[flyerId])
//     );
//   }

//   flyerIds = flyerIds.filter((flyerId) => checkIfFlyerIsActive(state, flyerId));

//   return flyerIds;
// };

export const filterFlyerNearestStore = (state, flyerId) => {
  const flyer = selectFlyerFromListOrCurrentFlyer(state, flyerId);
  if (!flyer || !flyer.stores) return null;
  const stores = Object.values(flyer.stores).sort(sortBy('distance', 'ASC'));
  return stores?.[0] || null;
};

export const hasNearestStore = (state, flyerId) => Boolean(filterFlyerNearestStore(state, flyerId));

export const selectFlyersToShowInGrid = (state, locale) => {
  let flyers = selectFlyersArray(state);

  // const appCustomLabel = selectCustomLabel(state);
  // if (appCustomLabel) {
  //   const customLabelFlyerIds = selectFlyerIdsByCustomLabel(state, appCustomLabel);
  //   flyers = flyers.filter((flyer) => customLabelFlyerIds.includes(flyer.id));
  // }

  const flyersMap = flyers
    .map((flyer) => {
      const endDateHidden = selectPropertyEndDateHidden(state);
      const stores = nearestStoresByFlyerId(state, flyer.id);
      const nearestStore = filterFlyerNearestStore(state, flyer.id);
      const retailerLogo = getRetailerLogo(flyer.retailer, locale);

      const flyerMap = {
        id: flyer.id,
        distance: flyer.distance,
        title: flyer.title,
        end_date: flyer.end_date,
        endDateHidden: endDateHidden,
        retailer: { name: flyer.retailer?.name, logo: retailerLogo },
        stores: [...stores],
        otherStores: flyer.otherStores,
        fetchedStores: flyer.fetchedStores,
        type: flyer.type,
        sub_type: flyer.sub_type,
        custom_position: flyer.extras.custom_position,
        settings: flyer.settings,
      };

      if (nearestStore) {
        flyerMap.nearestStore = {
          city: nearestStore.city,
          address: nearestStore.address,
        };
      }
      return flyerMap;
    })
    .sort(sortByPriorityAndDistance);
  return flyersMap;
};

export const selectCurrentFlyerNumberOfPages = (state) => {
  const flyer = selectCurrentFlyer(state);

  return flyer?.lastPubblication?.settings?.number_of_pages || 0;
};

export const selectCurrentFlyerIsActiveRetailMedia = createSelector(selectCurrentFlyer, (flyer) =>
  Boolean(+flyer?.is_active_retail_media || false),
);

export const selectFlyerBottomOffset = createSelector(
  selectCurrentFlyerIsActiveRetailMedia,
  selectIsMobile,
  (isActiveRetailMedia, isMobile) => {
    if (!isActiveRetailMedia) return 0;

    return getBottomRetailMediaBannerSize(isMobile).height;
  },
);

export const selectCurrentFlyerRetailerAssetsObject = createSelector(
  selectCurrentFlyer,
  (flyer) => flyer?.retailer?.assets || {},
);

function getImageUrl(retailerAssets, fields) {
  return fields.map((assetName) => retailerAssets[assetName]?.url).filter(Boolean)[0];
}

export const selectRetailMediaCentralBackgroundUrls = createSelector(
  selectCurrentFlyerRetailerAssetsObject,
  (retailerAssets) => {
    const desktopFields = ['retail_media_central_desktop_webp', 'retail_media_central_desktop'];
    const mobileFields = ['retail_media_central_mobile_webp', 'retail_media_central_mobile'];

    const small = getImageUrl(retailerAssets, mobileFields);
    const large = getImageUrl(retailerAssets, desktopFields);

    if (!small && !large) return null;

    return {
      small,
      large,
    };
  },
);

export const selectRetailMediaBottomBannerBackgroundUrl = createSelector(
  selectCurrentFlyerRetailerAssetsObject,
  (retailerAssets) => {
    const assetNames = ['retail_media_banner_mobile_webp', 'retail_media_banner_mobile'];

    const assetName = assetNames.find((assetName) => retailerAssets[assetName]?.url);
    if (!assetName) return null;

    return retailerAssets[assetName]?.url;
  },
);
